import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Cache for SEO settings to avoid repeated API calls
const seoCache = new Map();
let globalSeoSettings = null;
let brandingSettings = null;

/**
 * SEOHead Component
 * Fetches and applies SEO meta tags for the current page.
 * Falls back to global settings, then branding settings if page-specific settings are not configured.
 * 
 * @param {string} pagePath - The route path (e.g., "/login", "/signup")
 * @param {string} defaultTitle - Fallback title if no SEO settings found
 * @param {string} defaultDescription - Fallback description if no SEO settings found
 */
const SEOHead = ({ pagePath, defaultTitle = '', defaultDescription = '' }) => {
  const [seoSettings, setSeoSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const lookupPath = (pagePath || '/').split('?')[0].split('#')[0] || '/';

    const fetchSeoSettings = async () => {
      setLoading(true);
      try {
        // Check cache first
        if (seoCache.has(lookupPath)) {
          if (!cancelled) {
            setSeoSettings(seoCache.get(lookupPath));
            setLoading(false);
          }
          return;
        }

        // Fetch branding settings if not already cached (for fallback)
        if (!brandingSettings) {
          try {
            const brandResponse = await axios.get(`${API}/site-settings/brand`);
            if (brandResponse.data) {
              brandingSettings = brandResponse.data;
            }
          } catch (err) {
          }
        }

        // Fetch page-specific SEO settings (backend walks parent paths for dynamic IDs)
        const pathForUrl = lookupPath === 'global'
          ? 'global'
          : lookupPath.replace(/^\//, '');
        const response = await axios.get(`${API}/site-settings/seo/${pathForUrl}`);
        const pageSettings = response.data;

        // If page has settings configured (title exists), use them
        if (pageSettings && pageSettings.title) {
          seoCache.set(lookupPath, pageSettings);
          if (!cancelled) setSeoSettings(pageSettings);
        } else {
          // Fetch global settings if not already cached
          if (!globalSeoSettings) {
            try {
              const globalResponse = await axios.get(`${API}/site-settings/seo/global`);
              if (globalResponse.data && globalResponse.data.title) {
                globalSeoSettings = globalResponse.data;
              }
            } catch (err) {
              // Global settings might not exist, that's okay
            }
          }
          
          // Use global settings as fallback
          if (globalSeoSettings) {
            seoCache.set(lookupPath, globalSeoSettings);
            if (!cancelled) setSeoSettings(globalSeoSettings);
          } else {
            // Use branding settings as final fallback
            const siteName = brandingSettings?.site_name || 'Provisoft';
            const tagline = brandingSettings?.tagline || '';
            const finalTitle = defaultTitle || `${siteName}${tagline ? ' - ' + tagline : ''}`;
            const finalDescription = defaultDescription || tagline || `${siteName} - Workforce Management Platform`;
            
            if (!cancelled) {
              setSeoSettings({
                title: finalTitle,
                meta_description: finalDescription
              });
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch SEO settings:', error);
        // Use branding as fallback on error
        const siteName = brandingSettings?.site_name || 'Provisoft';
        const tagline = brandingSettings?.tagline || '';
        const finalTitle = defaultTitle || `${siteName}${tagline ? ' - ' + tagline : ''}`;
        const finalDescription = defaultDescription || tagline || '';
        
        if (!cancelled) {
          setSeoSettings({
            title: finalTitle,
            meta_description: finalDescription
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSeoSettings();
    return () => { cancelled = true; };
  }, [pagePath, defaultTitle, defaultDescription]);

  // Don't render anything while loading to prevent flashing
  if (loading || !seoSettings) {
    return null;
  }

  const {
    title,
    meta_description,
    keywords,
    og_title,
    og_description,
    og_image,
    twitter_card,
    twitter_title,
    twitter_description,
    twitter_image,
    canonical_url,
    robots
  } = seoSettings;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      {title && <title>{title}</title>}
      {meta_description && <meta name="description" content={meta_description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      {robots && <meta name="robots" content={robots} />}
      
      {/* Canonical URL */}
      {canonical_url && <link rel="canonical" href={canonical_url} />}
      
      {/* Open Graph / Facebook */}
      {(og_title || title) && <meta property="og:title" content={og_title || title} />}
      {(og_description || meta_description) && <meta property="og:description" content={og_description || meta_description} />}
      {og_image && <meta property="og:image" content={og_image} />}
      <meta property="og:type" content="website" />
      
      {/* Twitter */}
      {twitter_card && <meta name="twitter:card" content={twitter_card} />}
      {(twitter_title || og_title || title) && <meta name="twitter:title" content={twitter_title || og_title || title} />}
      {(twitter_description || og_description || meta_description) && <meta name="twitter:description" content={twitter_description || og_description || meta_description} />}
      {(twitter_image || og_image) && <meta name="twitter:image" content={twitter_image || og_image} />}
    </Helmet>
  );
};

// Utility function to clear SEO cache (useful when settings are updated)
export const clearSeoCache = () => {
  seoCache.clear();
  globalSeoSettings = null;
  brandingSettings = null;
};

export default SEOHead;
